import { SearchInput } from '@/components/catalog/SearchInput';
import { SortSelect } from '@/components/catalog/SortSelect';
import { TopFilters } from '@/components/catalog/TopFilters';
import { FilterState, getInitialFilters } from '@/components/filters';
import { Filters } from '@/components/filters/Filters';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductTable } from '@/components/product/ProductTable';
import { BackToTop } from '@/components/ui/BackToTop';
import { Button } from '@/components/ui/Button';
import { useReloadOnBack } from '@/hooks/useReloadOnBack';
import Layout from '@/layouts/Layout';
import { useLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/authStore';
import { useQueryFilter } from '@/stores/useQueryFilterStore';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { HomePageProps, InertiaPage } from '@/types';
import { FormDataConvertible } from '@inertiajs/core';
import { InfiniteScroll, router } from '@inertiajs/react';
import { isEmpty } from 'lodash';
import {
    Clock,
    FilterX,
    LayoutGrid,
    List,
    SlidersHorizontal,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { route } from 'ziggy-js';

const PRODUCT_GRID_LAYOUT =
    'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

const HomePage: InertiaPage<HomePageProps> = ({
    categories,
    products,
    beerStyles,
    vendors,
    packagingTypes,
    volumes,
    lastUpdate,
}: HomePageProps) => {
    useReloadOnBack({ only: ['cart'] });
    const { viewMode, setViewMode } = useSettingsStore();

    const { initializeQueryFilter } = useQueryFilter();
    const qp = route().queryParams;
    const { isAuthenticated } = useAuth();
    const { t } = useLanguage();

    useEffect(() => {
        initializeQueryFilter();
        router.on('success', () => {
            const url = new URL(window.location.href);
            const pageNumber = url.searchParams.get('page');
            if (pageNumber) {
                window.sessionStorage.setItem('lastVisitedPage', pageNumber);
            }
        });
    }, []);

    useEffect(() => {}, []);

    // Filters ..
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const initialFilters: FilterState = qp?.filters
        ? getInitialFilters(qp?.filters)
        : {};
    const [filters, setFilters] = useState<FilterState>(initialFilters);

    const buildFiltersData = (
        filters: FilterState,
    ): Record<string, FormDataConvertible> => {
        const filtersData: Record<string, FormDataConvertible> = {};

        Object.keys(filters).forEach((key) => {
            const value = filters[key as keyof FilterState];

            if (Array.isArray(value) && value.length > 0) {
                // Для массивов
                filtersData[`filters[${key}]`] = value.join(',');
            } else if (typeof value === 'number' && value !== 0) {
                // Для чисел
                filtersData[`filters[${key}]`] = value;
            }
        });

        return filtersData;
    };

    const handleApplyFilters = () => {
        router.get(route('home'), buildFiltersData(filters), {});
    };

    return (
        <>
            <div className="flex-1 px-4 pb-4 md:px-8 md:pb-8">
                <div className="mb-8 space-y-4">
                    <div className="flex flex-col justify-between gap-6 xl:flex-row xl:items-center">
                        <div>
                            <div className="mt-2 flex items-center gap-1.5 text-zinc-600">
                                <Clock className="h-3 w-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">
                                    Последнее обновление: {lastUpdate}
                                </p>
                            </div>
                            <p className="mt-2 text-sm font-medium text-zinc-500">
                                {t('common.found')} {products?.total}{' '}
                                {t('common.items')}
                            </p>
                        </div>

                        <div className="flex flex-1 flex-col items-center gap-3 md:flex-row">
                            <SearchInput
                                placeholder={t('common.searchPlaceholder')}
                                className="md:w-90 h-10 w-full"
                            />

                            <SortSelect />

                            <div className="flex w-full items-center gap-2 sm:w-auto">
                                <div className="flex h-10 shrink-0 items-center rounded-lg border border-zinc-800 bg-zinc-900 p-1">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={cn(
                                            'flex h-8 w-8 items-center justify-center rounded-md p-1.5 transition-all',
                                            viewMode === 'grid'
                                                ? 'bg-zinc-800 text-brand-gold shadow-sm'
                                                : 'text-zinc-500 hover:text-zinc-300',
                                        )}
                                    >
                                        <LayoutGrid className="h-4.5 w-4.5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={cn(
                                            'flex h-8 w-8 items-center justify-center rounded-md p-1.5 transition-all',
                                            viewMode === 'table'
                                                ? 'bg-zinc-800 text-brand-gold shadow-sm'
                                                : 'text-zinc-500 hover:text-zinc-300',
                                        )}
                                    >
                                        <List className="h-4.5 w-4.5" />
                                    </button>
                                </div>

                                <Button
                                    variant="secondary"
                                    className={cn(
                                        'h-10 flex-1 border border-zinc-800 bg-zinc-900 md:hidden',
                                        !isEmpty(filters)
                                            ? 'text-brand-gold'
                                            : 'text-zinc-300 hover:text-white',
                                    )}
                                    onClick={() => setIsSidebarOpen(true)}
                                >
                                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                                    {t('filters.title')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <TopFilters
                    categoriesById={categories}
                    beerStylesById={beerStyles}
                    vendorsById={vendors}
                    packagingTypesById={packagingTypes}
                    volumesById={volumes}
                    filters={filters}
                    setFilters={setFilters}
                    onApply={handleApplyFilters}
                />

                <div className="md:hidden">
                    <Filters
                        isOpen={isSidebarOpen}
                        onClose={() => setIsSidebarOpen(false)}
                        categoriesById={categories}
                        beerStylesById={beerStyles}
                        vendorsById={vendors}
                        packagingTypesById={packagingTypes}
                        volumesById={volumes}
                        filters={filters}
                        setFilters={setFilters}
                        onApply={handleApplyFilters}
                    />
                </div>

                {products.data.length > 0 ? (
                    <>
                        {viewMode === 'grid' ? (
                            <InfiniteScroll
                                data="products"
                                buffer={30}
                                onlyNext={true}
                                loading={() => 'Загрузка...'}
                                className={cn(
                                    'animate-in fade-in grid gap-6 duration-500',
                                    PRODUCT_GRID_LAYOUT,
                                )}
                            >
                                {products.data.map((product) => (
                                    <ProductCard
                                        key={product.sku_code}
                                        variation={product}
                                        isAuthorized={isAuthenticated}
                                    />
                                ))}
                            </InfiniteScroll>
                        ) : (
                            <div className="animate-in fade-in duration-500">
                                <ProductTable variations={products} />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-800 bg-brand-moss/5 py-20 text-center">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-zinc-900">
                            <FilterX className="h-8 w-8 text-zinc-500" />
                        </div>
                        <h3 className="text-xl font-bold text-zinc-300">
                            {t('filters.nothingFound')}
                        </h3>
                    </div>
                )}
            </div>

            {/* Global Back to Top Button */}
            <BackToTop />
        </>
    );
};

HomePage.layout = (page: React.ReactNode) => <Layout>{page}</Layout>;

export default HomePage;
